import { useState, useEffect } from 'react';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '@/components/ui/select';
import { Loader2, User } from 'lucide-react';
import { supabase } from '@/utils/supabase';

interface LoanOfficer {
  id: string;
  email: string;
  first_name?: string;
  last_name?: string;
  full_name?: string;
}

interface LoanOfficerSelectProps {
  value?: string;
  onChange: (officerId: string) => void;
  label?: string;
  placeholder?: string;
  includeUnassigned?: boolean;
  className?: string;
}

export function LoanOfficerSelect({
  value,
  onChange,
  label = "Assigned Loan Officer",
  placeholder = "Select a loan officer",
  includeUnassigned = true,
  className = ""
}: LoanOfficerSelectProps) {
  const [officers, setOfficers] = useState<LoanOfficer[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetchLoanOfficers();
  }, []);

  const fetchLoanOfficers = async () => {
    try {
      setLoading(true);
      setError(null);

      // Get the current user's organization
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
throw new Error('No authenticated user');
}

      const { data: currentProfile } = await supabase
        .from('users')
        .select('organization_id')
        .eq('id', user.id)
        .single();

      if (!currentProfile) {
throw new Error('User profile not found');
}

      // Fetch all loan officers in the organization
      const { data, error } = await supabase
        .from('users')
        .select('id, email, first_name, last_name, full_name, roles')
        .eq('organization_id', currentProfile.organization_id)
        .eq('employment_status', 'active')
        .order('full_name');

      // Filter to only include users with loan officer, admin, or manager roles
      const filteredData = data?.filter(user =>
        user.roles && (
          user.roles.includes('loan_officer') ||
          user.roles.includes('admin') ||
          user.roles.includes('manager')
        )
      );

      if (error) {
throw error;
}

      setOfficers(filteredData || []);
    } catch (err) {
      console.error('Error fetching loan officers:', err);
      setError(err instanceof Error ? err.message : 'Failed to load loan officers');
    } finally {
      setLoading(false);
    }
  };

  const getOfficerDisplayName = (officer: LoanOfficer) => {
    if (officer.full_name) {
return officer.full_name;
}
    if (officer.first_name && officer.last_name) {
      return `${officer.first_name} ${officer.last_name}`;
    }
    if (officer.first_name) {
return officer.first_name;
}
    if (officer.last_name) {
return officer.last_name;
}
    return officer.email;
  };

  if (loading) {
    return (
      <div className={`space-y-2 ${className}`}>
        {label && <Label>{label}</Label>}
        <div className="flex items-center justify-center h-10 border rounded-md">
          <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={`space-y-2 ${className}`}>
        {label && <Label>{label}</Label>}
        <div className="text-sm text-red-600">
          Error loading loan officers: {error}
        </div>
      </div>
    );
  }

  return (
    <div className={`space-y-2 ${className}`}>
      {label && <Label>{label}</Label>}
      <Select 
        value={value || "unassigned"} 
        onValueChange={(newValue) => {
          // Convert "unassigned" back to empty string
          onChange(newValue === "unassigned" ? "" : newValue);
        }}
      >
        <SelectTrigger>
          <SelectValue placeholder={placeholder}>
            {value && officers.find(o => o.id === value) ? (
              <div className="flex items-center gap-2">
                <User className="h-4 w-4" />
                {getOfficerDisplayName(officers.find(o => o.id === value)!)}
              </div>
            ) : (
              placeholder
            )}
          </SelectValue>
        </SelectTrigger>
        <SelectContent>
          {includeUnassigned && (
            <SelectItem value="unassigned">
              <div className="flex items-center gap-2">
                <User className="h-4 w-4 opacity-50" />
                Unassigned
              </div>
            </SelectItem>
          )}
          {officers.map((officer) => (
            <SelectItem key={officer.id} value={officer.id}>
              <div className="flex items-center gap-2">
                <User className="h-4 w-4" />
                {getOfficerDisplayName(officer)}
              </div>
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
}